// Guided venue setup (F-HQ-03, ADR-0047; spec/14 §3.2, spec/13 §13.1): the rules that turn what a venue and its
// organisation hold into which setup steps are done, which are still to do, and whether the venue may go live.
//
// The server reads the facts — opening hours on the venue record, a price somebody chose, a PC paired, a receipt
// printer added, a session run — and hands them here; the console shows what comes back. Nothing in this file reads a
// clock or a database. The step ids are the contract's (`@clavian/contracts/constants`), so the API, the console and
// these rules can never name a step differently.
//
// **The essentials are the Go-live gate** (the product owner, 2026-10-08): opening hours set, a pass players can buy
// (ADR-0058, the product owner 2026-10-12: "it is an essential step to create a gaming pass"), a PC paired. Everything else is suggested — worth doing, never required — and may be skipped; a skipped step that is
// later done reads `done`. Essentials cannot be skipped. The organisation's own essential is a first venue (with none
// there is nothing to open); its business details are suggested — a venue needs them for a GST registration, not to
// open (ADR-0046, ADR-0047).
import { ESSENTIAL_VENUE_SETUP_STEPS, isEssentialVenueSetupStep, isSuggestedOrganizationSetupStep, ORGANIZATION_SETUP_STEPS, VENUE_SETUP_STEPS, } from '@clavian/contracts/constants';
// ---------------------------------------------------------------------------------------------
// One rule per step
// ---------------------------------------------------------------------------------------------
/** Opening hours are set: at least one day of the regular week is open. Holidays alone do not count. */
export function openingHoursSet(hours) {
    return Object.values(hours).some((ranges) => Array.isArray(ranges) && ranges.length > 0);
}
/**
 * Players have a pass they can buy: one is on sale at the venue (ADR-0058). Pay as you go, on or off, with whatever
 * rate, does not make the step done: the product owner made a pass the essential (2026-10-12), and a new venue starts
 * with pay as you go off.
 */
export function passOnSale(price) {
    return price.passesOnSale > 0;
}
/**
 * A PC is paired. A venue whose stations are all staff-run — consoles, sims, tables, no PC at all — has nothing to
 * pair, so its stations being there is enough (ADR-0047 §3, the open question it records).
 */
export function stationReady(stations) {
    if (stations.pairedPcs > 0)
        return true;
    return stations.pcs === 0 && stations.stations > 0;
}
/** Which essentials are done. */
export function essentialStepsDone(facts) {
    return {
        openingHours: openingHoursSet(facts.openingHours),
        price: passOnSale(facts.price),
        stationPaired: stationReady(facts.stations),
    };
}
/** Which suggested steps are done. */
export function suggestedStepsDone(facts) {
    return {
        taxMode: facts.taxRegistrationRecorded,
        lockdownTest: facts.lockdownTestPassed,
        printer: facts.receiptPrinter,
        payments: facts.counterUpi || facts.onlinePayments,
        kioskSignUp: facts.kioskSignUpChosen,
        offlineCodes: facts.offlineCodesIssued || facts.offlineSettingsChosen,
        staffPin: facts.staffPinSet,
        staffInvited: facts.staffBesidesOwners,
        firstSession: facts.sessionRun,
    };
}
/** The organisation's own steps: a first venue, and its business details (legal name and registered address). */
export function organizationStepsDone(facts) {
    return {
        businessDetails: facts.businessDetailsComplete,
        firstVenue: facts.venues > 0,
    };
}
// ---------------------------------------------------------------------------------------------
// Steps, statuses and progress
// ---------------------------------------------------------------------------------------------
export function venueStepKind(step) {
    return isEssentialVenueSetupStep(step) ? 'essential' : 'suggested';
}
export function organizationStepKind(step) {
    return isSuggestedOrganizationSetupStep(step) ? 'suggested' : 'essential';
}
/**
 * Every venue step, essentials first, with its status: `done` when the facts say so, else `skipped` when the venue
 * skipped it (a suggested step only — a skip recorded against an essential is ignored), else `todo`. `done` may be
 * partial: a step it leaves out is `todo` (HQ reads the essentials alone).
 */
export function venueSetupSteps(done, skips = new Map()) {
    return VENUE_SETUP_STEPS.map((id) => {
        const kind = venueStepKind(id);
        const skip = kind === 'suggested' ? (skips.get(id) ?? null) : null;
        const status = done[id] === true ? 'done' : skip !== null ? 'skipped' : 'todo';
        return { id, kind, status, skip };
    });
}
/**
 * The organisation's own steps, essential first, with their status — as a venue's: `done` when the facts say so, else
 * `skipped` when the organisation skipped it (its suggested step only; a skip recorded against the first venue is
 * ignored), else `todo`.
 */
export function organizationSetupSteps(facts, skips = new Map()) {
    const done = organizationStepsDone(facts);
    return ORGANIZATION_SETUP_STEPS.map((id) => {
        const kind = organizationStepKind(id);
        const skip = kind === 'suggested' ? (skips.get(id) ?? null) : null;
        const status = done[id] ? 'done' : skip !== null ? 'skipped' : 'todo';
        return { id, kind, status, skip };
    });
}
/** How far along a list of steps is: "2 of 3 essentials · 4 of 9 suggested". */
export function setupProgress(steps) {
    const essentials = steps.filter((step) => step.kind === 'essential');
    const suggested = steps.filter((step) => step.kind === 'suggested');
    return {
        essentialsDone: essentials.filter((step) => step.status === 'done').length,
        essentialsTotal: essentials.length,
        suggestedSettled: suggested.filter((step) => step.status !== 'todo').length,
        suggestedTotal: suggested.length,
        left: steps.filter((step) => step.status === 'todo').length,
    };
}
/**
 * The essentials still missing, in order — what Go live is refused for. Empty: the venue may go live. Reads the
 * essentials' facts alone, so the gate never depends on a suggested step or a skip.
 */
export function missingEssentials(facts) {
    const done = essentialStepsDone(facts);
    return ESSENTIAL_VENUE_SETUP_STEPS.filter((step) => !done[step]);
}
/** May a venue in this status move to `live`? Only `setup → live` is gated; `paused → live` (resume) never is. */
export function goLiveRefusal(currentStatus, nextStatus, missing) {
    if (currentStatus !== 'setup' || nextStatus !== 'live')
        return [];
    return missing;
}
/** The organisation's essential steps still missing, in order. A suggested one — done, skipped or not — never counts. */
function ownMissingEssentials(organization) {
    return organization.filter((step) => step.kind === 'essential' && step.status !== 'done').map((step) => step.id);
}
/**
 * Where an organisation stands, for HQ's list ("3 of 4 · stuck at pairing a PC"): its own essential (a first venue)
 * counted with the essentials of the venue nearest to ready — the one missing fewest, the oldest on a tie. Suggested
 * steps are never counted. Archived venues are not passed in.
 */
export function organizationSetupSummary(organization, venues) {
    const ownEssentials = organization.filter((step) => step.kind === 'essential');
    const ownMissing = ownMissingEssentials(organization);
    const ownDone = ownEssentials.length - ownMissing.length;
    const essentialsTotal = ownEssentials.length + ESSENTIAL_VENUE_SETUP_STEPS.length;
    const nearest = [...venues].sort((a, b) => a.missingEssentials.length - b.missingEssentials.length || a.createdAt - b.createdAt)[0];
    const venueDone = nearest === undefined ? 0 : ESSENTIAL_VENUE_SETUP_STEPS.length - nearest.missingEssentials.length;
    const state = venues.some((venue) => venue.status === 'live' || venue.status === 'paused') ? 'live' : venues.length === 0 ? 'no_venue' : 'setup';
    const nextStep = ownMissing[0] ?? nearest?.missingEssentials[0] ?? null;
    return { state, essentialsDone: ownDone + venueDone, essentialsTotal, nextStep: state === 'live' ? null : nextStep };
}
/**
 * The essentials left across an organisation, for the shell's "Setup · n left": its own essential (a first venue) if
 * missing, and each venue still in `setup`'s missing essentials. Suggested steps never count — they would nag forever —
 * and a venue that has opened adds nothing.
 */
export function setupEssentialsLeft(organization, venues) {
    const own = ownMissingEssentials(organization).length;
    return own + venues.filter((venue) => venue.status === 'setup').reduce((sum, venue) => sum + venue.missingEssentials.length, 0);
}
//# sourceMappingURL=steps.js.map